//Login.js
import { trackEvent } from "../firebase/analytics";
import { useEffect, useRef, useState } from "react";
import { loginUser, resetPassword } from "../firebase/auth";
import { useNavigate } from "react-router-dom";
import "../styles/Login.css";
import Oiiko from "../tools/oiikotranslogo2.png";
import StatusBanner from "../components/StatusBanner";
import { formatError } from "../utils/errorMessages";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  // Forgot-password modal state
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  const [resendAt, setResendAt] = useState(0); // throttle resends
  const resetInputRef = useRef(null);

  const navigate = useNavigate();

  const isValidEmail = (em) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((em || "").trim());

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    trackEvent("login_attempt", {
      page: "login",
    });
    try {
      const user = await loginUser(email, password);
      if (!user) {
        trackEvent("login_error", {
          error_code: "account_not_found",
        });
        setError({
          title: "Account Not Found",
          message:
            "We couldn’t find an account with this email. Please contact your employer or Oiiko Support.",
        });
        return;
      }

      if (user.status === "Pending") {
        navigate("/reset-password");
        return;
      }
      if (user.profileCompleted === false && user.selfOnboarding === true) {
        navigate("/onboarding");
        return;
      }
      if (user.role === "employer") {
        // ✅ ADD THIS HERE
        trackEvent("login_success", {
          role: user.role || "unknown",
        });
        navigate("/admin-dashboard");
      } else if (user.role === "employee") {
        // ✅ ADD THIS HERE
        trackEvent("login_success", {
          role: user.role || "unknown",
        });
        navigate("/employee-dashboard");
      } else {
        trackEvent("login_error", {
          error_code: "invalid_role",
        });
        setError({
          title: "Invalid Role",
          message:
            "Your account role is not recognized. Please contact your administrator.",
        });
      }
    } catch (err) {
      trackEvent("login_error", {
        error_code: err.code || "unknown",
      });
      const { title, message } = formatError("login", err);
      setError({ title, message });
    }
  };

  // ----- Forgot password modal handlers
  const openReset = () => {
    setResetEmail(email || "");
    setResetError("");
    setResetMsg("");
    setShowReset(true);
  };
  const closeReset = () => setShowReset(false);

  useEffect(() => {
    if (showReset && resetInputRef.current) resetInputRef.current.focus();
    const onEsc = (e) => e.key === "Escape" && setShowReset(false);
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [showReset]);

  const secondsLeft = Math.max(0, Math.ceil((resendAt - Date.now()) / 1000));

  const submitReset = async (e) => {
    e?.preventDefault?.();
    setResetError("");
    setResetMsg("");

    if (!isValidEmail(resetEmail)) {
      setResetError("Enter a valid email address.");
      return;
    }
    if (secondsLeft > 0) return; // throttle

    setResetLoading(true);
    try {
      await resetPassword(resetEmail);
      setResetMsg(
        "If an account exists for this email, we’ve sent a reset link.",
      );
      setResendAt(Date.now() + 60_000); // 60s throttle
    } catch (err) {
      setResetError(err.message || "Could not send reset email.");
    } finally {
      setResetLoading(false);
    }
  };

  // ----- Support: open default email client with prefilled subject/body
  const handleSupportMail = () => {
    const to = "support@oiiko.app"; // change if needed
    const subject = "Oiiko Support – Login help";
    const lines = [
      "Hi Oiiko Support,",
      "",
      "I need help signing in.",
      "",
      `Email: ${email || "(not provided)"}`,
      `Browser: ${navigator.userAgent}`,
      `Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
      "",
      "Details:",
    ];
    const mailto = `mailto:${to}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(lines.join("\n"))}`;
    window.location.href = mailto;
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <img src={Oiiko} alt="Oiiko Logo" className="companylogin-logo" />

        <div className="login-box">
          <h1 className="login-title">Welcome Back to Oiiko!</h1>
          <p className="login-subtext">
            Sign in to manage your workforce and streamline operations with
            Oiiko.
          </p>

          <form onSubmit={handleLogin}>
            <label>Work Email Address</label>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <label>Enter Your Secure Password</label>
            <div className="input-group">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <span
                className="toggle-visibility"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? "Hide" : "Show"}
              </span>
            </div>

            <button type="submit" className="loginclicks">
              Log In to Your Account
            </button>
            <button
              type="button"
              className="register-button"
              onClick={() => {
                trackEvent("create_account_click", {
                  page: "login",
                });
                navigate("/register");
              }}
            >
              Create an Oiiko Account
            </button>
          </form>

          <div className="links">
            <p onClick={openReset} role="button" tabIndex={0}>
              Forgot your password? Reset it here.
            </p>
            <p onClick={handleSupportMail} role="button" tabIndex={0}>
              Need help? Contact Support.
            </p>
          </div>

          {error && (
            <StatusBanner
              type="error"
              title={error.title}
              message={error.message}
              onClose={() => setError("")}
            />
          )}

          {message && (
            <StatusBanner
              type="success"
              title="Success"
              message={message}
              onClose={() => setMessage("")}
            />
          )}
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showReset && (
        <div className="modal-overlay" onClick={closeReset}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reset-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={closeReset}
              aria-label="Close"
            >
              ×
            </button>
            <h2 id="reset-title">Reset your password</h2>
            <p className="modal-sub">
              Enter the email linked to your Oiiko account. We’ll send you a
              password reset link.
            </p>

            <form onSubmit={submitReset}>
              <label htmlFor="reset-email">Work Email Address</label>
              <input
                id="reset-email"
                ref={resetInputRef}
                type="email"
                placeholder="you@company.com"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className={resetError ? "input-error" : ""}
                required
              />
              {resetError && <div className="field-error">{resetError}</div>}
              {resetMsg && <div className="success-message">{resetMsg}</div>}

              <div className="modal-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={resetLoading || secondsLeft > 0}
                >
                  {resetLoading
                    ? "Sending…"
                    : secondsLeft > 0
                    ? `Resend in ${secondsLeft}s`
                    : "Send Reset Link"}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={closeReset}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
